import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { CircleCheckbox } from '../../../../../components/_events/_manage-event/shared/circle-checkbox/circle-checkbox';
import { IAssetsSpeakersDetailsDTO, ITitleAssetUrlDTO } from '../../../../../api/assets.dao';
import { AssetsFileFormWrapper } from '../assets-file-form/assets-file-form-wrapper';
import { FileInput } from '../../shared/file-input/file-input';
import { DIMENSIONS_PROFILE_IMAGE, SUPPORTED_IMAGE_TYPES } from '../../../../../constants/file.constants';
import { droppableAsset } from '../assets';
import {
    ASSETS_PROFILE_IMAGE_TEXT,
    ASSETS_DROP_TO_REPLACE_TEXT,
    ASSETS_DROP_TO_ADD_TEXT,
    ASSETS_ADD_PROFILE_IMAGE_TEXT,
    ASSETS_ADDED_TEXT,
} from '../assets.text';

import '../assets.scss';

interface IAssetBiographyProps {
    accountId: number;
    eventId: number;
    organizationId: number;
    attendee: IAssetsSpeakersDetailsDTO;
    updateAttendeePermission: boolean;
    currentDroppableElementId: number;
    uploadProfileImage: (file: File, organizationId: number, attendeeId: number) => Promise<void>;
    onDeleteAsset: (organizationId: number, attendeeId: number) => void;
}

const AssetSpeakerItem = mobxReact.observer((props: IAssetBiographyProps) => {
    const {
        accountId,
        eventId,
        organizationId,
        attendee,
        updateAttendeePermission,
        currentDroppableElementId,
        uploadProfileImage,
        onDeleteAsset
    } = props;

    const isDisabled = !!attendee.isLoadingProfileImage;
    const profileImageIsPresent = !!attendee?.profileImageWithUrl?.title;
    const isDraggingElementHovered = currentDroppableElementId === attendee.attendeeId;

    const handleProfileImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        uploadProfileImage(e.target.files[0], organizationId, attendee.attendeeId);
    };

    return (
        <tr
            className={classnames('assets-list-item',
                { 'assets-list-item__hovered--adding': isDraggingElementHovered && !profileImageIsPresent },
                { 'assets-list-item__hovered--replacing': isDraggingElementHovered && profileImageIsPresent },
            )}
            data-value={attendee.attendeeId}
            data-default={droppableAsset}
            data-attendee={attendee.attendeeId}
            data-organization={organizationId}
            data-image={profileImageIsPresent}
        >
            <td className='hoverable'>
                <div className='assets-list-item__attendee-name'>
                    <CircleCheckbox
                        item={{ active: profileImageIsPresent }}
                        handleClick={null}
                        size={20}
                        className='disabled'
                    />
                    <div className='overflow-ellipsis'>
                        {`${attendee.firstName} ${attendee.lastName} `}
                        <FormattedMessage {...ASSETS_PROFILE_IMAGE_TEXT} />
                    </div>
                </div>
            </td>
            <td className='hoverable'>
                {profileImageIsPresent
                    ? (isDraggingElementHovered
                        ? <i><FormattedMessage {...ASSETS_DROP_TO_REPLACE_TEXT} /></i>
                        : <AssetsFileFormWrapper
                            accountId={accountId}
                            eventId={eventId}
                            organizationId={organizationId}
                            attendeeId={attendee.attendeeId}
                            file={{ ...attendee.profileImageWithUrl, ...attendee.profileImage } as ITitleAssetUrlDTO}
                            onDeleteAsset={() => onDeleteAsset(organizationId, attendee.attendeeId)}
                            uploadProfileImage={uploadProfileImage}
                        />)
                    : <>
                        {updateAttendeePermission &&
                            (isDraggingElementHovered
                                ? <i><FormattedMessage {...ASSETS_DROP_TO_ADD_TEXT} /></i>
                                : <FileInput
                                    className='m-right-5px assets-list-item__upload-button'
                                    inputClassName='droppable'
                                    type='default'
                                    title={<FormattedMessage {...ASSETS_ADD_PROFILE_IMAGE_TEXT} />}
                                    acceptedFileTypes={SUPPORTED_IMAGE_TYPES.join()}
                                    onChange={handleProfileImageFileChange}
                                    disabled={isDisabled}
                                />
                            )
                        }
                    </>
                }
            </td>
            <td className='hoverable'>
                {attendee.validationProfileImageError
                    ? <div className='oe-form-field__error overflow-ellipsis'>
                        {attendee.validationProfileImageError}
                    </div>
                    : <div className='overflow-ellipsis assets-list-item__hint'>
                        PNG, JPG, GIF or SVG. Min {DIMENSIONS_PROFILE_IMAGE} by {DIMENSIONS_PROFILE_IMAGE} pixels
                    </div>
                }
            </td>
            <td className='hoverable'/>
            <td className='hoverable'>
                {attendee.isAdded && profileImageIsPresent
                    && <div className='assets-list-item__added-status assets-list-item__added-status__fade-out'>
                        <CircleCheckbox
                            item={{ active: true }}
                            handleClick={null}
                            size={20}
                            className='disabled opacity-circle'
                        />
                        <span>
                            <FormattedMessage {...ASSETS_ADDED_TEXT} />
                        </span>
                    </div>
                }
            </td>
        </tr>
    );

});

export { AssetSpeakerItem };